import { useContext, useState } from 'react';

import {
  IconUser,
  IconLock,
  IconShieldCheck,
  IconCheck,
  IconHelpCircle,
  IconMail,
  IconLogout,
  IconShieldHalfFilled,
} from '@tabler/icons-react';
import config from 'config';
import { AuthContext, AuthContextAction } from 'context/AuthContext';
import { UserModel } from 'features/pages/account/User';
import { useNavigate } from 'react-router-dom';
import { Avatar, DropdownMenuItem } from 'ui-kit';
import { SupportModalV1 } from 'ui-kit/DraggableHelpButton/SupportModalV1';

import { useQuery } from '@apollo/client';

import { GET_USER_PERMISSIONS } from 'query/permissionsQueries';
import { performLogout, useUsername } from 'utils/authUtils';

const AccountRoutePrefix = '/account';

const menuItems = [
  {
    label: 'User',
    icon: <IconUser size={20} />,
    link: `${AccountRoutePrefix}?section=user`,
  },
  {
    label: 'Security',
    icon: <IconLock size={20} />,
    link: `${AccountRoutePrefix}?section=security`,
  },
  {
    label: 'User Role & Permissions',
    icon: <IconShieldCheck size={20} />,
    link: `${AccountRoutePrefix}?section=permissions`,
    adminOnly: true,
  },
  {
    label: 'Application Preferences',
    icon: <IconCheck size={20} />,
    link: `${AccountRoutePrefix}?section=preferences`,
  },
  {
    label: 'Email Preferences', // New Email Preferences menu item
    icon: <IconMail size={20} />,
    link: `${AccountRoutePrefix}?section=email-preferences`,
  },
  {
    label: 'Support',
    icon: <IconHelpCircle size={20} />,
  },
  {
    label: 'Trust Center',
    icon: <IconShieldHalfFilled size={20} />,
    link: 'https://app.vanta.com/orsnn.com/trust/z40h552xusg4m49zkgj57',
    openInNewTab: true,
  },
];

interface UserContextMenuProps {
  userInfo?: UserModel;
}

export const UserContextMenu = ({ userInfo }: UserContextMenuProps) => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lastLogin, setLastLogin] = useState<string>('');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const username = useUsername();

  useQuery(GET_USER_PERMISSIONS, {
    variables: { requestData: { username: username || '' } },
    skip: !username,
    onCompleted: (data) => {
      if (data.userManagementGetUser && data.userManagementGetUser.user) {
        setLastLogin(data.userManagementGetUser.user.lastLoginDate);
      }
    },
  });

  return (
    <div className="inline-flex w-max flex-col items-start gap-2.5 rounded bg-gray-950 py-2 shadow-[0px_4px_8px_0px_rgba(13,13,13,0.9),0px_0px_1px_0px_#0d0d0d] light:border light:border-gray-200 light:bg-white light:shadow-lg">
      <div className="flex items-start gap-2 px-4 py-2">
        <Avatar name={`${userInfo?.givenName || ''} ${userInfo?.familyName || ''}`} />
        <div className="flex flex-col text-xs font-medium leading-5 text-white light:text-gray-900">
          <span>{`${userInfo?.givenName || ''} ${userInfo?.familyName || ''}`.trim()}</span>
          <span className="text-xxs font-normal leading-3">{userInfo?.email || ''}</span>
          {/* border-slate-600/bg-slate-700: were $slate400 (#28303E) and
              $slate500 (#242933) -- Tailwind's slate scale is offset from
              the scss tokens by two steps (confirmed by hex, not by
              matching key name), so the correct matches are slate-600 and
              slate-700, not slate-500/slate-700. */}
          <div className="mt-2 flex flex-col items-start gap-0.5 self-stretch rounded-sm border border-slate-600 bg-slate-700 px-2 py-1 text-xxs font-normal leading-3 text-slate-300 light:border-gray-200 light:bg-gray-100 light:text-gray-700">
            <span>Latest login</span>
            <span>{lastLogin ? new Date(parseInt(lastLogin, 10)).toLocaleString() : ''}</span>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col items-start gap-2 self-stretch">
        {/* bg-slate-500: was $slate300 (#3B4351) -- same scss/tailwind
            slate offset, the correct match is slate-500, not slate-600. */}
        <hr className="m-0 h-px w-full border-none bg-slate-500 light:bg-gray-200" />
        {menuItems.map((m) => {
          if (m.label === 'Support') {
            return (
              <DropdownMenuItem
                key={m.label}
                label={m.label}
                icon={m.icon}
                onClick={() => setShowSupportModal(true)}
              />
            );
          }

          return (
            <DropdownMenuItem
              key={m.label}
              label={m.label}
              icon={m.icon}
              link={m.link}
              newTab={m.openInNewTab}
            />
          );
        })}
        {/* bg-slate-500: was $slate300 (#3B4351) -- same scss/tailwind
            slate offset, the correct match is slate-500, not slate-600. */}
        <hr className="m-0 h-px w-full border-none bg-slate-500 light:bg-gray-200" />
        <DropdownMenuItem
          label="Log Out"
          icon={<IconLogout size={20} />}
          onClick={async () => {
            // Revoke tokens globally; for SSO sessions this returns the Cognito
            // Hosted-UI logout URL so we can also clear the SSO session cookie.
            const ssoLogoutUrl = await performLogout(config.website.URL);
            dispatch({ type: AuthContextAction.SIGN_OUT });
            if (ssoLogoutUrl) {
              window.location.href = ssoLogoutUrl;
            } else {
              navigate('/');
            }
          }}
        />
      </div>

      {showSupportModal && <SupportModalV1 onClose={() => setShowSupportModal(false)} />}
    </div>
  );
};
